import React from 'react';

import { ChoiceChip } from '@components';
import {
  AvailableCategoryLabel,
  AvailableFilterOptions,
  AvailableFromLabel,
  AvailableProfileLabel,
  IAvailableCandidateFilters,
  toggle,
} from '@utils';
import {
  FilterSection,
  offersChoice,
  RegionFilterSection,
  SeniorityFilterSection,
} from 'components/candidate/CandidatesList/components/CandidateFilterSections';
import { ChipCount } from 'components/candidate/CandidatesList/components/PositionFilterChips';
import { ScoreFilterField } from './ScoreFilterField';
import { SkillFilterField } from './SkillFilterField';

export const offersAvailableFilterChoice = (options: AvailableFilterOptions) =>
  options.hasScores ||
  [
    options.primarySkills,
    options.skills,
    options.seniorities,
    options.regions,
  ].some(({ length }) => length > 0) ||
  [options.categories, options.availableFrom, options.profile].some(
    ({ length }) => offersChoice(length),
  );

interface IAvailableCandidateFilterSectionsProps {
  options: AvailableFilterOptions;
  filters: IAvailableCandidateFilters;
  onChange: (patch: Partial<IAvailableCandidateFilters>) => void;
}

export const AvailableCandidateFilterSections: React.FC<
  IAvailableCandidateFilterSectionsProps
> = ({ options, filters, onChange }) => (
  <>
    <FilterSection title="Category">
      {options.categories.map(({ category, count }) => (
        <ChoiceChip
          key={category}
          selected={filters.categories.includes(category)}
          onClick={() =>
            onChange({ categories: toggle(filters.categories, category) })
          }
        >
          {AvailableCategoryLabel[category]}
          <ChipCount>{count}</ChipCount>
        </ChoiceChip>
      ))}
    </FilterSection>

    {options.primarySkills.length > 0 && (
      <SkillFilterField
        title="Stack"
        label="stack"
        skills={options.primarySkills}
        selected={filters.primarySkills}
        onChange={(primarySkills) => onChange({ primarySkills })}
      />
    )}

    {options.skills.length > 0 && (
      <SkillFilterField
        title="Skills"
        label="skill"
        skills={options.skills}
        selected={filters.skills}
        onChange={(skills) => onChange({ skills })}
      />
    )}

    <SeniorityFilterSection
      seniorities={options.seniorities}
      selected={filters.seniorities}
      onChange={(seniorities) => onChange({ seniorities })}
      showsSingleOption
    />

    <RegionFilterSection
      regions={options.regions}
      selected={filters.regions}
      onChange={(regions) => onChange({ regions })}
      showsSingleOption
    />

    <FilterSection title="Available from">
      {options.availableFrom.map((availableFrom) => (
        <ChoiceChip
          key={availableFrom}
          selected={filters.availableFrom === availableFrom}
          onClick={() => onChange({ availableFrom })}
        >
          {AvailableFromLabel[availableFrom]}
        </ChoiceChip>
      ))}
    </FilterSection>

    <FilterSection title="Profile">
      {options.profile.map((option) => (
        <ChoiceChip
          key={option}
          selected={filters.profile.includes(option)}
          onClick={() => onChange({ profile: toggle(filters.profile, option) })}
        >
          {AvailableProfileLabel[option]}
        </ChoiceChip>
      ))}
    </FilterSection>

    {options.hasScores && (
      <ScoreFilterField
        value={filters.ttMin}
        onChange={(ttMin) => onChange({ ttMin })}
      />
    )}
  </>
);
